import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Popover, Select } from "antd"
import { useTranslation } from "react-i18next"
import { SettingOutlined } from "@ant-design/icons"
import moment from "moment"
import styles from "../../Calendar.module.css"

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: moment(i, "H").format("h A")
}))

const END_HOUR_OPTIONS = [...HOUR_OPTIONS, { value: 24, label: "12 AM" }]

const TimeRangeSettings = ({ startHour, endHour, onStartChange, onEndChange, onReset, isModified }) => {
  const { t } = useTranslation()

  const startOptions = useMemo(() => HOUR_OPTIONS.filter((o) => o.value < endHour), [endHour])
  const endOptions = useMemo(() => END_HOUR_OPTIONS.filter((o) => o.value > startHour), [startHour])

  const content = (
    <div className={styles.timeRangePopover}>
      <div className={styles.timeRangePopoverHeader}>
        <span className={styles.timeRangePopoverTitle}>{t("calendar.visible_time_range", "Visible Time Range")}</span>
        <div className={styles.timeRangePopoverSubtitle}>{t("calendar.filter_by_time_slot", "Filter bookings by time slot")}</div>
      </div>
      <Flex gap={12} className={styles.timeRangePopoverBody}>
        <Flex vertical gap={4} className={styles.timeRangePopoverField}>
          <span className={styles.timeRangePopoverLabel}>{t("calendar.time_range_from", "From")}</span>
          <Select value={startHour} onChange={onStartChange} options={startOptions} className={styles.timeRangeSelect} />
        </Flex>
        <Flex vertical gap={4} className={styles.timeRangePopoverField}>
          <span className={styles.timeRangePopoverLabel}>{t("calendar.time_range_to", "To")}</span>
          <Select value={endHour} onChange={onEndChange} options={endOptions} className={styles.timeRangeSelect} />
        </Flex>
      </Flex>
      <div className={styles.timeRangePopoverFooter}>
        <Button color="primary" variant="outlined" disabled={!isModified} onClick={onReset} block>
          {t("calendar.reset_to_default", "Reset to Default")}
        </Button>
      </div>
    </div>
  )

  return (
    <Popover trigger="click" placement="bottomRight" arrow={false} overlayClassName={styles.timeRangePopoverOverlay} content={content}>
      <Button icon={<SettingOutlined className={styles.settingIcon} />} type="text" size="large" />
    </Popover>
  )
}

TimeRangeSettings.propTypes = {
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number.isRequired,
  onStartChange: PropTypes.func.isRequired,
  onEndChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isModified: PropTypes.bool.isRequired
}

export default TimeRangeSettings
